import Header from '../Header/Header';
import { Loader, SuccessModal, ConfirmationPopup } from '@ohif/ui';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, ErrorMessage, Field } from 'formik';
import * as Yup from 'yup';
import { AppDispatch, RootState } from '@custom/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { addActiveMachine } from '@custom/rtk-apis/activeMachine/addActivemachine';
import { customStyle } from '@custom/Utils/utils';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls } from '@custom/Utils/constants/apiUrls';
import { useTheme } from "../../ui/src/contextProviders";
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';

const AddEditActiveMachine = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const machineData = location.state?.machinesData;

  const data = useSelector((state: RootState) => state.addActiveMachineSliceSlice);
  const modalityStudyResponse = useSelector(
    (state: RootState) => state?.getModalityStudySliceResponse
  );

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [commonData, setCommonData] = useState({ modality: [] });
  const [modalityData, setModalityData] = useState([]);
  const [edit, setIsEdit] = useState(false);
  const [modal, setModal] = useState(false);
  const [formData, setFormData] = useState();
  const [machineFields, setMachineFields] = useState([]);

  const [initialValues, setInitialValues] = useState({
    _id: "",
    machineType: '',
    totalMachine: '',
    machineDetails: [],
  });

  useEffect(() => {
    dispatch(getModalityStudy());
    fetchCommonData();
  }, [])

  useEffect(() => {
    if (modalityStudyResponse?.status === 'SUCCESS') {
      const modalities = modalityStudyResponse?.result?.modalities;
      const uniqueModalities = Array.from(new Set(modalities));
      const modalityList = uniqueModalities?.map(modality => ({
        label: modality,
        value: modality
      }));
      setModalityData(modalityList);
    }
  }, [modalityStudyResponse?.status]);

  useEffect(() => {
    if (machineData) {
      setIsEdit(true);
      
      const total = Number(machineData.totalMachine);
      const details = machineData.machines || [];

      const machineDetailsArray = Array(total).fill(null).map((_, i) => ({
        aeTitle: details[i]?.aeTitle || '',
        machineName: details[i]?.machineName || '',
        isEnabled: details[i]?.status === 'enabled'
      }));

      setInitialValues({
        _id: machineData._id,
        machineType: machineData.machineType || '',
        totalMachine: total,
        machineDetails: machineDetailsArray
      });

      setMachineFields(machineDetailsArray);
    } else {
      setIsEdit(false);
    }
  }, [machineData]);

  const validationSchema = Yup.object().shape({
    machineType: Yup.string().required('Machine Type is required'),
    totalMachine: Yup.number()
      .required('Total No. of Machines is required')
      .min(0, 'Total No. of Machines cannot be negative'),
    machineDetails: Yup.array().of(
      Yup.object().shape({
        aeTitle: Yup.string().required('AE Title is required'),
        machineName: Yup.string().required('Machine Name is required'),
        isEnabled: Yup.boolean().required('Status is required')
      })
    )
  });

  const handleFieldChange = (index, fieldName, value, setFieldValue) => {
    const updatedFields = [...machineFields];
    updatedFields[index][fieldName] = value;
    setMachineFields(updatedFields);
    setFieldValue('machineDetails', updatedFields);
  };

  const handleRadioChange = (index, value, setFieldValue) => {
    const updatedFields = [...machineFields];
    updatedFields[index].isEnabled = value === 'enable';
    setMachineFields(updatedFields);
    setFieldValue('machineDetails', updatedFields);
  };

  const handleSaveForm = async (values, { resetForm }) => {
    if (edit) {
      setFormData(values); // Store form values
      setIsPopupOpen(true); // Open the confirmation popup
    } else {
      await handleDispatch(values);
    }
  };

  const handleDispatch = async (formValues) => {
    const response = await dispatch(addActiveMachine({
      ...formValues,
      _id: edit ? formValues._id : undefined, // Include _id only if in edit mode
      machineType: formValues.machineType,
      totalMachine: Number(formValues.totalMachine),
      machines: formValues.machineDetails.map((machine, index) => ({
        machineName: machine.machineName,
        aeTitle: machine.aeTitle,
        status: machine.isEnabled ? 'enabled' : 'disabled'
      }))
    }));
    if (response?.payload) {
      setModal(true); // Open the success modal
      setTimeout(() => {
        setModal(false); // Close modal after 2 seconds
        navigate('/frontdeskmachine'); // Navigate back
      }, 2000);
    }
  };

  const handleConfirm = async () => {
    setIsPopupOpen(false); // Close confirmation popup
    await handleDispatch(formData); // Dispatch the update action
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const cancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate('/frontdeskmachine');
  };

  const fetchCommonData = async () => {
    try {
      const commonData: any = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' })
      setCommonData(commonData?.result);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const isDarkMode = useTheme();

  return (
    <div className='page page__full' style={
      isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
    }>
      <Header isBack={true} title={edit ? 'Edit Machine Type' : 'Add Machine Type'} />
      <div className='page__body'>
        <div className='details'>
          <div className='form form--innersec'>
            <section className='form__section'>
              <h1 className='form__section__title'>Machine Details</h1>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSaveForm}
                enableReinitialize={true}
              >
                {({ errors, values, handleChange, handleBlur, setFieldValue }) => 
                  { 
                    const handleTotalMachineChange = (e, setFieldValue) => {
                      const newTotal = Number(e.target.value);
                      const currentTotal = Number(values.totalMachine); // Get current total from form values

                      if (newTotal > currentTotal) {
                        // Preserve existing machineFields and append empty fields for new machines
                        const additionalFields = Array(newTotal - currentTotal).fill().map(() => ({
                          aeTitle: '',
                          machineName: '',
                          isEnabled: true
                        }));

                        const updatedFields = [...machineFields, ...additionalFields];
                        setMachineFields(updatedFields);
                        setFieldValue('machineDetails', updatedFields);
                      } else if (newTotal < currentTotal) {
                        // If total is decreased, truncate the array
                        const updatedFields = machineFields.slice(0, newTotal);
                        setMachineFields(updatedFields);
                        setFieldValue('machineDetails', updatedFields);
                      }

                      setFieldValue('totalMachine', newTotal);
                    };
                  return (
                  <Form>
                    <div className='formRow formRow__3'>
                      <div className='formCol'>
                        <div className='form__Field'>
                          <label className='form__label' htmlFor='machineType'>
                            Machine Type<span className='required-asterisk'>*</span>
                          </label>
                          <select
                            className='form__input form__input--sm'
                            id='machineType'
                            name='machineType'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.machineType}
                          >
                            <option value='' label='Select machine type' />
                            {modalityData?.map((modality, index) => (
                              <option key={index} value={modality.value}>
                                {modality.value}
                              </option>
                            ))}
                          </select>
                          <ErrorMessage name='machineType' component='div' className='error text-error' />
                        </div>
                      </div>
                      <div className='formCol'>
                        <div className='form__Field'>
                          <label className='form__label' htmlFor='totalMachine'>
                            Total No. of Machines<span className='required-asterisk'>*</span>
                          </label>
                          <input
                            className='form__input form__input--sm'
                            type='number'
                            id='totalMachine'
                            name='totalMachine'
                            placeholder='Total No. of Machines'
                            onChange={(e) => handleTotalMachineChange(e, setFieldValue)}
                            onBlur={handleBlur}
                            value={values.totalMachine === 0 ? '' : values.totalMachine}
                            autoComplete='off'
                          />
                          <ErrorMessage name='totalMachine' component='div' className='error text-error' />
                        </div>
                      </div>
                    </div>

                    {machineFields.length > 0 && (
                      <>
                        <h3 className='form__section__title'>Machine Details</h3>
                        <div className='formRow formRow__3'>
                            {machineFields.map((field, index) => (
                                <>
                                  <div className='formCol'>
                                    <label className='form__label' htmlFor={`machineDetails.${index}.aeTitle`}>
                                      AE Title {index + 1}<span className='required-asterisk'>*</span>
                                    </label>
                                    <input
                                      className='form__input form__input--sm'
                                      type='text'
                                      id={`machineDetails.${index}.aeTitle`}
                                      name={`machineDetails.${index}.aeTitle`}
                                      placeholder={`AE Title ${index + 1}`}
                                      value={field.aeTitle}
                                      onChange={(e) => handleFieldChange(index, 'aeTitle', e.target.value, setFieldValue)}
                                      onBlur={handleBlur}
                                      autoComplete='off'
                                    />
                                    <ErrorMessage 
                                      name={`machineDetails.${index}.aeTitle`} 
                                      component='div' 
                                      className='error text-error' 
                                    />
                                  </div>
                                  <div className='formCol'>
                                    <label className='form__label' htmlFor={`machineDetails.${index}.machineName`}>
                                      Machine Name {index + 1}<span className='required-asterisk'>*</span>
                                    </label>
                                    <input
                                      className='form__input form__input--sm'
                                      type='text'
                                      id={`machineDetails.${index}.machineName`}
                                      name={`machineDetails.${index}.machineName`}
                                      placeholder={`Machine Name ${index + 1}`}
                                      value={field.machineName}
                                      onChange={(e) => handleFieldChange(index, 'machineName', e.target.value, setFieldValue)}
                                      onBlur={handleBlur}
                                      autoComplete='off'
                                    />
                                    <ErrorMessage 
                                      name={`machineDetails.${index}.machineName`} 
                                      component='div' 
                                      className='error text-error' 
                                    />
                                  </div>
                                  <div className='formCol'>
                                    <label className='form__label' htmlFor={`machineDetails.${index}.isEnabled`}>
                                      Status<span className='required-asterisk'>*</span>
                                    </label>
                                    <div className="form__radio form__radio--gray mt-4">
                                      <div className="form__radio__option">
                                        <input
                                          type="radio"
                                          name={`machineDetails.${index}.isEnabled`}
                                          value="enable"
                                          className="form__radio__input"
                                          checked={field.isEnabled === true}
                                          onChange={() => handleRadioChange(index, 'enable', setFieldValue)}
                                        />
                                        <label className="form__radio__label">Enable</label>
                                      </div>
                                      <div className="form__radio__option">
                                        <input
                                          type="radio"
                                          name={`machineDetails.${index}.isEnabled`}
                                          value="disable"
                                          className="form__radio__input"
                                          checked={field.isEnabled === false}
                                          onChange={() => handleRadioChange(index, 'disable', setFieldValue)}
                                        />
                                        <label className="form__radio__label">Disable</label>
                                      </div>
                                    </div>
                                  </div>
                                </>
                            ))}
                        </div>
                      </>
                    )}

                    <div className='btn__group'>
                      <button className='btn btn__primary' type='submit'>
                        {edit ? 'Update' : 'Save'}
                      </button>
                      <button className='btn btn__white' onClick={cancel}>
                        Cancel
                      </button>
                    </div>
                  </Form>
                )}}
              </Formik>
            </section>
          </div>
        </div>
      </div>
      <Loader isLoaderOpen={data.loading} />
      <ConfirmationPopup
        title={"Update Details!"}
        isOpen={isPopupOpen}
        message={`Are you sure you want update the changes? `}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <SuccessModal
        isSuccessModalOpen={modal}
        title={edit ? "Active machine updated successfully." : "Active machine saved successfully."}
        handleCloseModal={() => setModal(false)}
      />
    </div>
  );
};

export default AddEditActiveMachine;
